/**
 * Guest API
 * Unified client-side API for guest access cloud functions
 *
 * The external worker is identified by the personal link token in the URL;
 * every task action also names the task (`GuestTaskRef`) and the server checks
 * that the worker is delegated it. Used by the guest portal and detail pages
 * for external contractors, without authentication.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
    GuestApiRequest,
    GuestApiResponse,
    GuestTaskRef,
    GuestReportIssuePayload,
    ProjectViewData,
    WorkOrderViewData,
    PortalData,
    PhotoType,
    DeleteUploadPayload,
    CompleteWorkOrderPayload,
    AssessIssuePayload,
    AssessIssueResult,
} from '../types/guestAccess';
import { guestLinkRefusalFromError } from './guestLinkRefusal';

/**
 * Cached URL entry with expiration
 */
interface CachedUrl {
    url: string;
    expiresAt: number;
}

/**
 * Unified API for guest operations (portal, work orders and projects)
 */
class GuestApi {
    // Use the pre-initialized Firebase functions from firebase.ts

    /**
     * Cache for signed download URLs
     * URLs are valid for 60 minutes, we cache for 50 minutes to be safe
     */
    private urlCache = new Map<string, CachedUrl>();
    private static URL_CACHE_TTL_MS = 50 * 60 * 1000; // 50 minutes

    private async call<R extends GuestApiResponse>(request: GuestApiRequest): Promise<R> {
        const guestApiCallable = httpsCallable<GuestApiRequest, GuestApiResponse>(
            functions,
            'guestApi'
        );
        try {
            const result = await guestApiCallable(request);
            return result.data as R;
        } catch (err) {
            throw guestLinkRefusalFromError(err) ?? err;
        }
    }

    // ============================================================================
    // Portal and link self-service
    // ============================================================================

    /**
     * Everything delegated to the link's user: open work orders, open projects
     * and recently completed items, plus the link's validity.
     */
    async getPortal(token: string): Promise<PortalData> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'getPortal' }>>({
            token,
            action: 'getPortal',
        });
        return response.data;
    }

    /**
     * Expired link: ask the admins who delegated the open tasks to extend the
     * access. Grants nothing; `alreadyRequested` when asked within 24 hours.
     */
    async requestExtension(token: string): Promise<{ alreadyRequested: boolean }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'requestExtension' }>>({
            token,
            action: 'requestExtension',
        });
        return response.data;
    }

    /**
     * Suspected leak: revoke this link and e-mail a new one. With nothing
     * delegated the link is only revoked (`newLinkSent: false`); the next
     * delegation brings a new one.
     */
    async reportLink(token: string): Promise<{ maskedEmail: string; newLinkSent: boolean }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'reportLink' }>>({
            token,
            action: 'reportLink',
        });
        return response.data;
    }

    /** Replaced or legacy link: e-mail the user's current link. */
    async requestCurrentLink(token: string): Promise<{ maskedEmail: string }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'requestCurrentLink' }>>({
            token,
            action: 'requestCurrentLink',
        });
        return response.data;
    }

    /**
     * The förvaltare's under/över decision on a pending felanmälan (#1653).
     * Under creates a work order delegated to them; över leaves the issue to
     * the styrelse. A handled issue is refused with `failed-precondition`.
     */
    async assessIssue(token: string, payload: AssessIssuePayload): Promise<AssessIssueResult> {
        const comment = payload.comment?.trim();
        const response = await this.call<Extract<GuestApiResponse, { action: 'assessIssue' }>>({
            token,
            action: 'assessIssue',
            payload: { issueKey: payload.issueKey, decision: payload.decision, ...(comment ? { comment } : {}) },
        });
        return response.data;
    }

    // ============================================================================
    // Work Order Actions
    // ============================================================================

    /**
     * Get work order data with building context
     */
    async getWorkOrder(token: string, workOrderKey: string): Promise<WorkOrderViewData> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'getWorkOrder' }>>({
            token,
            action: 'getWorkOrder',
            payload: { workOrderKey },
        });
        return response.data;
    }

    /**
     * Complete a work order as a guest
     */
    async completeWorkOrder(
        token: string,
        payload: CompleteWorkOrderPayload,
        workOrderKey: string
    ): Promise<{ message: string }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'completeWorkOrder' }>>({
            token,
            action: 'completeWorkOrder',
            payload: { ...payload, workOrderKey },
        });
        return response.data;
    }

    /** Signed upload URL for a felanmälan photo under `issues/{issueKey}/photos/` */
    async getIssueUploadUrl(
        token: string,
        workOrderKey: string,
        issueKey: string,
        fileName: string,
        mimeType: string
    ): Promise<{ uploadUrl: string; key: string; storagePath: string }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'getIssueUploadUrl' }>>({
            token,
            action: 'getIssueUploadUrl',
            payload: { issueKey, fileName, mimeType, workOrderKey },
        });
        return response.data;
    }

    /** Create a felanmälan from the work order (photos already uploaded) */
    async reportIssue(token: string, workOrderKey: string, payload: GuestReportIssuePayload): Promise<string> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'reportIssue' }>>({
            token,
            action: 'reportIssue',
            payload: { ...payload, workOrderKey },
        });
        return response.data.issueKey;
    }

    // ============================================================================
    // Project Actions
    // ============================================================================

    /**
     * Get project data with building context
     */
    async getProject(token: string, projectKey: string): Promise<ProjectViewData> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'getProject' }>>({
            token,
            action: 'getProject',
            payload: { projectKey },
        });
        return response.data;
    }

    /**
     * Set project completion status (idempotent)
     * External user can mark their work as complete with an optional note
     */
    async setProjectCompletion(token: string, projectKey: string, completed: boolean, note?: string): Promise<void> {
        await this.call({
            token,
            action: 'setProjectCompletion',
            payload: { completed, note, projectKey },
        });
    }

    // ============================================================================
    // Notes
    // ============================================================================

    /**
     * Add a note to the task
     */
    async addNote(token: string, task: GuestTaskRef, title: string, content: string): Promise<string> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'addNote' }>>({
            token,
            action: 'addNote',
            payload: { title, content, ...task },
        });
        return response.data.noteKey;
    }

    /**
     * Update an existing note
     */
    async updateNote(token: string, task: GuestTaskRef, noteKey: string, title: string, content: string): Promise<void> {
        await this.call({
            token,
            action: 'updateNote',
            payload: { noteKey, title, content, ...task },
        });
    }

    /**
     * Delete a note (only own notes)
     */
    async deleteNote(token: string, task: GuestTaskRef, noteKey: string): Promise<void> {
        await this.call({
            token,
            action: 'deleteNote',
            payload: { noteKey, ...task },
        });
    }

    // ============================================================================
    // Checklists
    // ============================================================================

    /**
     * Create a new checklist
     */
    async createChecklist(token: string, task: GuestTaskRef, title: string, items?: string[]): Promise<string> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'createChecklist' }>>({
            token,
            action: 'createChecklist',
            payload: { title, items, ...task },
        });
        return response.data.checklistKey;
    }

    /**
     * Add an item to a checklist
     */
    async addChecklistItem(token: string, task: GuestTaskRef, checklistKey: string, text: string): Promise<string> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'addChecklistItem' }>>({
            token,
            action: 'addChecklistItem',
            payload: { checklistKey, text, ...task },
        });
        return response.data.itemKey;
    }

    /**
     * Set checklist item completion status
     */
    async setChecklistItemCompleted(
        token: string,
        task: GuestTaskRef,
        checklistKey: string,
        itemKey: string,
        completed: boolean
    ): Promise<void> {
        await this.call({
            token,
            action: 'setChecklistItemCompleted',
            payload: { checklistKey, itemKey, completed, ...task },
        });
    }

    /**
     * Delete a checklist item (only own items)
     */
    async deleteChecklistItem(token: string, task: GuestTaskRef, checklistKey: string, itemKey: string): Promise<void> {
        await this.call({
            token,
            action: 'deleteChecklistItem',
            payload: { checklistKey, itemKey, ...task },
        });
    }

    /**
     * Delete an entire checklist (only own checklists)
     */
    async deleteChecklist(token: string, task: GuestTaskRef, checklistKey: string): Promise<void> {
        await this.call({
            token,
            action: 'deleteChecklist',
            payload: { checklistKey, ...task },
        });
    }

    // ============================================================================
    // Photos & Attachments
    // ============================================================================

    /**
     * Get a signed upload URL for a file (photos/attachments)
     */
    async getUploadUrl(
        token: string,
        task: GuestTaskRef,
        fileName: string,
        mimeType: string,
        photoType?: PhotoType
    ): Promise<{ uploadUrl: string; key: string; storagePath: string }> {
        const response = await this.call<Extract<GuestApiResponse, { action: 'getUploadUrl' }>>({
            token,
            action: 'getUploadUrl',
            payload: { fileName, mimeType, photoType, ...task },
        });
        return response.data;
    }

    /**
     * Register an uploaded file (photos or attachments)
     * Server sets author and date
     */
    async registerUpload(
        token: string,
        task: GuestTaskRef,
        file: {
            key: string;
            storagePath: string;
            width: number;
            height: number;
            fileSize: number;
            photoType?: PhotoType;
            fileName?: string;
            mimeType?: string;
        }
    ): Promise<void> {
        await this.call({
            token,
            action: 'registerUpload',
            payload: { ...file, ...task },
        });
    }

    /**
     * Delete a photo (only own photos)
     */
    async deletePhoto(token: string, task: GuestTaskRef, photoType: PhotoType, key: string): Promise<void> {
        const payload: DeleteUploadPayload & GuestTaskRef = { type: 'photo', photoType, key, ...task };
        await this.call({
            token,
            action: 'deleteUpload',
            payload,
        });
    }

    /**
     * Delete an attachment (only own attachments)
     */
    async deleteAttachment(token: string, task: GuestTaskRef, attachmentKey: string): Promise<void> {
        const payload: DeleteUploadPayload & GuestTaskRef = { type: 'attachment', attachmentKey, ...task };
        await this.call({
            token,
            action: 'deleteUpload',
            payload,
        });
    }

    /**
     * Get a signed download URL for a file
     * Uses client-side caching to avoid redundant Cloud Function calls
     */
    async getDownloadUrl(token: string, task: GuestTaskRef, storagePath: string): Promise<string> {
        // Cache per (task, storagePath) — the server validates the path against
        // the named task, so the same path can be authorized for a work order
        // but not the project (and vice versa).
        const cacheKey = `${task.workOrderKey ?? ''}:${task.projectKey ?? ''}:${storagePath}`;

        // Check cache first
        const cached = this.urlCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
            return cached.url;
        }

        // Fetch new URL
        const response = await this.call<Extract<GuestApiResponse, { action: 'getDownloadUrl' }>>({
            token,
            action: 'getDownloadUrl',
            payload: { storagePath, ...task },
        });

        // Cache the URL
        this.urlCache.set(cacheKey, {
            url: response.data.downloadUrl,
            expiresAt: Date.now() + GuestApi.URL_CACHE_TTL_MS,
        });

        return response.data.downloadUrl;
    }

    /**
     * Clear the URL cache (useful when navigating away or on errors)
     */
    clearUrlCache(): void {
        this.urlCache.clear();
    }
}

export const guestApi = new GuestApi();
